import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, Clock, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { SharedNav, SharedFooter, Reveal } from '../components/SharedLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { lang } = useLanguage();
  useEffect(() => { axios.post(`${API}/track`, { event_type: 'page_view', page: '/contact' }).catch(() => {}); }, []);

  const trackCall = () => axios.post(`${API}/track`, { event_type: 'call_now_click', page: '/contact' }).catch(() => {});
  const trackDir = () => axios.post(`${API}/track`, { event_type: 'get_directions_click', page: '/contact' }).catch(() => {});

  return (
    <>
      <Helmet>
        <title>Contact Lone Star Bonding Co. | 24/7 Houston Bail Bonds | (713) 228-8800</title>
        <meta name="description" content="Contact Lone Star Bonding Co. for 24/7 bail bond service in Houston TX. Call (713) 228-8800. Located at 1420 Washington Ave, Houston TX 77002. Se habla espanol." />
        <link rel="canonical" href="https://lonestarbonding.com/contact" />
      </Helmet>
      <SharedNav />

      <section style={{ paddingTop: '10rem', paddingBottom: '4rem', background: 'var(--void)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: 700 }}>
          <Reveal>
            <span className="section-header__label">{lang === 'es' ? 'CONTACTO' : 'CONTACT'}</span>
            <h1 className="section-header__title" style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)' }}>
              {lang === 'es' ? 'CONTACTENOS' : 'CONTACT US'}
            </h1>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'Disponibles 24 horas al dia — 7 dias a la semana — incluyendo dias festivos' : 'Available 24 Hours a Day — 7 Days a Week — Including Holidays'}
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: '4rem 2rem', background: 'var(--void)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Phone CTA */}
            <Reveal>
              <div style={{ padding: '2.5rem', background: 'var(--surface)', border: '2px solid rgba(235, 110, 31, 0.25)', textAlign: 'center', position: 'relative' }}>
                <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--astros-orange), var(--texas-red))' }} />
                <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'linear-gradient(135deg, var(--astros-orange), var(--texas-red))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 0 60px -15px rgba(235, 110, 31, 0.5)' }}>
                  <Phone size={30} color="white" />
                </div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'white', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                  {lang === 'es' ? 'LLAME AHORA' : 'CALL NOW'}
                </h2>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', color: 'var(--astros-orange)', marginBottom: '0.75rem', textShadow: '0 0 20px rgba(235, 110, 31, 0.3)' }}>
                  (713) 228-8800
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
                  {lang === 'es' ? 'Disponibles 24/7/365' : 'Available 24/7/365'}
                </p>
                <a href="tel:7132288800" onClick={trackCall} className="btn-emergency" data-testid="contact-call-btn"
                  style={{ width: '100%', justifyContent: 'center' }}>
                  <Phone size={18} /> {lang === 'es' ? 'LLAMAR AHORA' : 'CALL FOR HELP'}
                </a>
              </div>
            </Reveal>

            {/* Location */}
            <Reveal delay={0.1}>
              <div style={{ padding: '2.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  <MapPin size={22} style={{ color: 'var(--astros-orange)' }} />
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'white', letterSpacing: '0.08em' }}>
                    {lang === 'es' ? 'UBICACION' : 'LOCATION'}
                  </h3>
                </div>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'white', marginBottom: '0.3rem' }}>1420 Washington Ave</p>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Houston, TX 77002</p>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                  {lang === 'es' ? 'Cerca de los Tribunales Criminales del Condado de Harris' : 'Near Harris County Criminal Courts'}
                </p>
                <a href="https://maps.google.com/?q=1420+Washington+Ave+Houston+TX+77002" target="_blank" rel="noopener noreferrer"
                  onClick={trackDir} className="btn-primary" data-testid="contact-directions-btn"
                  style={{ width: '100%', justifyContent: 'center', marginBottom: '1.25rem' }}>
                  <MapPin size={16} /> {lang === 'es' ? 'DIRECCIONES' : 'GET DIRECTIONS'}
                </a>
                <div style={{ borderRadius: 4, height: 180, overflow: 'hidden', border: '1px solid var(--border-dim)' }}>
                  <iframe title="Location" src="https://maps.google.com/maps?q=1420+Washington+Ave+Houston+TX+77002&output=embed"
                    style={{ width: '100%', height: '100%', border: 'none' }} loading="lazy" />
                </div>
              </div>
            </Reveal>

            {/* Contact Info */}
            <Reveal delay={0.2}>
              <div style={{ padding: '2.5rem', background: 'var(--surface)', border: '1px solid var(--border-dim)', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'white', letterSpacing: '0.08em' }}>
                  {lang === 'es' ? 'INFORMACION' : 'INFO'}
                </h3>
                {[
                  { icon: <Phone size={18} />, label: lang === 'es' ? 'Telefono' : 'Phone', value: '(713) 228-8800', href: 'tel:7132288800' },
                  { icon: <Clock size={18} />, label: lang === 'es' ? 'Horas' : 'Hours', value: '24/7', href: null },
                  { icon: <MapPin size={18} />, label: lang === 'es' ? 'Direccion' : 'Address', value: '1420 Washington Ave\nHouston, TX 77002', href: null },
                  { icon: <Mail size={18} />, label: 'Email', value: 'lonestarbondingco@sbcglobal.net', href: 'mailto:lonestarbondingco@sbcglobal.net' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--astros-orange)', flexShrink: 0, background: 'rgba(235, 110, 31, 0.06)', border: '1px solid rgba(235, 110, 31, 0.15)' }}>
                      {item.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>{item.label}</div>
                      {item.href ? <a href={item.href} style={{ color: 'white', fontWeight: 600, textDecoration: 'none', whiteSpace: 'pre-line' }}>{item.value}</a>
                        : <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{item.value}</div>}
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 'auto', paddingTop: '1.5rem', borderTop: '1px solid var(--border-dim)' }}>
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.6 }}>
                    {lang === 'es' ? 'Complete los formularios en linea antes de venir.' : 'Complete forms online before coming in.'}
                  </p>
                  <Link to="/how-bail-works" className="btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                    <ArrowRight size={14} /> {lang === 'es' ? 'Como Funciona' : 'How Bail Works'}
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem', background: 'var(--surface)', borderTop: '1px solid var(--border-dim)', textAlign: 'center' }}>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          Lone Star Bonding Co. | Steve Cruz | Texas Bail Bond License No. 74629 | 1420 Washington Ave, Houston, TX 77002
        </p>
      </section>
      <SharedFooter />
    </>
  );
};

export default ContactPage;
